import { Picker as RNPicker } from '@react-native-picker/picker';
import { CheckIcon } from 'lucide-react-native';
import { isWeb, XStack, YStack } from 'tamagui';

import { useThemeColors } from '../../themes/SmoothieColorTokens';
import { Icon } from '../icons/Icon';
import { BodyLargeEmphasized } from '../text/Typography';

interface PickerProps<T> {
    value: T;
    options: Array<{
        label: string;
        value: T;
        enabled?: boolean;
    }>;
    onChange: (value: T) => void;
    onPressDone?: (selectedValue: T) => void;
    placeholder?: string;
    disabled?: boolean;
    size?: 'regular' | 'tiny';
}

export const PICKER_PLACEHOLDER: string = 'Select';

export const Picker = <T,>({
    value,
    options,
    onChange,
    onPressDone,
    placeholder,
    disabled,
    size,
}: PickerProps<T>) => {
    const { getColor } = useThemeColors();

    const style = isWeb
        ? {
              width: '100%',
              height: size === 'tiny' ? 32 : 56,
              borderRadius: size === 'tiny' ? 8 : 12,
              borderColor: disabled
                  ? getColor('$gray800')
                  : getColor('$gray500'),
              color: disabled ? getColor('$gray1100') : getColor('$black'),
              backgroundColor: disabled
                  ? getColor('$gray600')
                  : getColor('$gray200'),
              padding: size === 'tiny' ? 4 : 16,
              fontSize: size === 'tiny' ? 12 : 16,
          }
        : {
              width: '100%',
              minHeight: 300,
          };

    return (
        <YStack backgroundColor="$white">
            {!isWeb && (
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    gap={12}
                    borderBottomWidth={1}
                    borderTopWidth={1}
                    borderColor="$gray600"
                    paddingVertical={8}
                    onPress={() => {
                        onPressDone?.(value);
                    }}
                >
                    <Icon icon={CheckIcon} size={16} color="$black" />
                    <BodyLargeEmphasized color="$black" height={24}>
                        Done
                    </BodyLargeEmphasized>
                </XStack>
            )}
            <RNPicker
                selectedValue={value}
                onValueChange={onChange}
                enabled={!disabled}
                // @ts-expect-error width as a string throws an error, but it does work as expected
                style={style}
                itemStyle={{
                    fontSize: 18,
                }}
            >
                {placeholder != null && (
                    <RNPicker.Item
                        label={placeholder}
                        value={null}
                        color={getColor('$gray200')}
                    />
                )}
                {options.map(({ label, value, enabled }) => (
                    <RNPicker.Item
                        key={label}
                        label={label}
                        value={value}
                        enabled={enabled}
                    />
                ))}
            </RNPicker>
        </YStack>
    );
};
